// First-touch (last-click for gclid) capture of ad/source signals at app boot,
// persisted to localStorage so the anon→auth wizard journey carries it into the
// createDraft/sendDraft payload. Pure core (computeAttributionRecord) is unit-tested;
// the window/localStorage wrappers are best-effort and never throw.
// See docs/superpowers/specs/2026-07-14-betonijerry-lead-attribution-design.md
import { gtag } from "src/analytics/gtag";

const STORAGE_KEY = "betonijerry.attribution";

// URL param → stored key.
const PARAM_MAP = [
    ["gclid", "gclid"],
    ["gbraid", "gbraid"],
    ["wbraid", "wbraid"],
    ["utm_source", "utmSource"],
    ["utm_medium", "utmMedium"],
    ["utm_campaign", "utmCampaign"],
    ["utm_term", "utmTerm"],
    ["utm_content", "utmContent"],
];

/**
 * Pure: given the landing URL parts and the currently-stored record, return the
 * record that SHOULD be stored. Touch rule: a fresh gclid/gbraid/wbraid overwrites;
 * otherwise the existing record is kept (first-touch), or a new one is created when
 * nothing is stored yet.
 */
export function computeAttributionRecord({ search, referrer, pathname, stored, now }) {
    const params = new URLSearchParams(search || "");
    const picked = {};
    for (const [urlKey, outKey] of PARAM_MAP) {
        const v = params.get(urlKey);
        if (v) picked[outKey] = v;
    }
    const hasAdClick = Boolean(picked.gclid || picked.gbraid || picked.wbraid);
    if (!hasAdClick && stored) return stored; // preserve first touch
    return {
        ...picked,
        referrer: referrer || "",
        landingPath: pathname || "",
        capturedAt: now,
    };
}

function safeRead() {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

/** Read the landing URL, apply the touch rule, persist. Best-effort. */
export function captureAttribution(nowIso) {
    if (typeof window === "undefined") return;
    try {
        const stored = safeRead();
        const record = computeAttributionRecord({
            search: window.location.search,
            referrer: typeof document !== "undefined" ? document.referrer : "",
            pathname: window.location.pathname,
            stored,
            now: nowIso || new Date().toISOString(),
        });
        if (record !== stored) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch {
        // best-effort; never block boot
    }
}

/** The stored attribution record, or null. */
export function getAttribution() {
    if (typeof window === "undefined") return null;
    return safeRead();
}

/**
 * Best-effort: merge the GA4 client_id into the stored record once gtag.js has it.
 * No-op without a GA4 id / gtag / granted consent. Never throws.
 */
export function attachGaClientId() {
    const gaId = import.meta.env.VITE_GA4_ID || "";
    if (!gaId || typeof window === "undefined") return;
    try {
        gtag("get", gaId, "client_id", (cid) => {
            if (!cid) return;
            try {
                const rec = safeRead() || {};
                rec.gaClientId = cid;
                window.localStorage.setItem(STORAGE_KEY, JSON.stringify(rec));
            } catch {
                // best-effort
            }
        });
    } catch {
        // best-effort
    }
}
